import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import QRCode from 'qrcode';
import { Button, CodeInput, Field } from '@paige/ui';
import { trackAction } from '../../analytics/mutation-events';
import {
  beginTotpEnrolment,
  secondStepMessage,
  type TotpEnrolment as Enrolment,
} from '../../auth/second-factor';
import { EmailConfirmStep } from './EmailConfirmStep';
import { addressUnconfirmed, useEmailConfirm } from './use-email-confirm';

export interface TotpEnrolmentProps {
  /** Called once the authenticator app has been verified and is enrolled. */
  onDone: () => void;
  onCancel?: (() => void) | undefined;
}

/** Scan the QR (or type the key) into an authenticator app, then prove it with one code. */
export function TotpEnrolment({ onDone, onCancel }: TotpEnrolmentProps): ReactNode {
  const { enrolment, qr, error, busy, code, setCode, verify, email } = useEnrolment(onDone);
  if (email.asking)
    return (
      <EmailConfirmStep
        busy={email.busy}
        error={email.error}
        onConfirm={(c) => void email.confirm(c)}
        resend={email.resend}
        onCancel={onCancel}
      />
    );
  return (
    <form className="totp" onSubmit={(e) => void verify(e)} noValidate>
      <Scan qr={qr} secretKey={enrolment?.secretKey ?? null} />
      <CodeField code={code} error={error} onChange={setCode} />
      <div className="totp__actions">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
            Not now
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          loading={busy}
          disabled={!enrolment || code.length !== 6}
        >
          Verify and turn on
        </Button>
      </div>
    </form>
  );
}

/** The QR to scan, with the key beside it for typing by hand. */
function Scan({ qr, secretKey }: { qr: string | null; secretKey: string | null }): ReactNode {
  return (
    <div className="totp__scan">
      {qr ? (
        <img className="totp__qr" src={qr} alt="QR code for your authenticator app" />
      ) : (
        <span className="totp__qr totp__qr--pending" aria-hidden="true" />
      )}
      <div className="totp__how">
        <p>Open your authenticator app and scan this code.</p>
        {secretKey && (
          <p className="totp__key">
            <span>Or enter the key by hand</span>
            <code>{spaced(secretKey)}</code>
          </p>
        )}
      </div>
    </div>
  );
}

/** The six digits the app shows right now. */
export function CodeField({
  code,
  error,
  onChange,
  onComplete,
  label = 'The 6-digit code your app shows',
  autoFocus = false,
}: {
  code: string;
  error: string | null;
  onChange: (code: string) => void;
  /** Fires once when the sixth digit lands, for a step that continues on its own. */
  onComplete?: ((code: string) => void) | undefined;
  label?: string;
  autoFocus?: boolean;
}): ReactNode {
  return (
    <Field label={label} error={error ?? undefined}>
      {({ id, describedBy }) => (
        <CodeInput
          id={id}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          autoFocus={autoFocus}
          value={code}
          onChange={onChange}
          onComplete={onComplete}
        />
      )}
    </Field>
  );
}

function useEnrolment(onDone: () => void) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  // Begun again once the address is confirmed: the sign-in service refuses a second step until then.
  const [round, setRound] = useState(0);
  const email = useEmailConfirm(() => setRound((n) => n + 1));
  const { enrolment, qr, error, setError } = useBegun(round, email.ask);
  const verify = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!enrolment || code.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      await enrolment.finish(code);
      trackAction('account.two_step_enrolled');
      onDone();
    } catch (e) {
      setError(secondStepMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return { enrolment, qr, error, busy, code, setCode, verify, email };
}

/** The enrolment the sign-in service began, with its QR; an account whose address nobody confirmed is asked for that first. */
function useBegun(round: number, askForEmail: () => Promise<void>) {
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    beginTotpEnrolment()
      .then(async (e) => {
        const url = await QRCode.toDataURL(e.uri, { margin: 1, width: 176 });
        if (!live) return;
        setEnrolment(e);
        setQr(url);
      })
      .catch((e: unknown) => {
        if (!live) return;
        if (addressUnconfirmed(e)) void askForEmail();
        else setError(secondStepMessage(e));
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- begun once, and again after each confirmation
  }, [round]);
  return { enrolment, qr, error, setError };
}

/** Groups of four, as authenticator apps print keys. */
function spaced(key: string): string {
  return key.replace(/(.{4})/g, '$1 ').trim();
}
